import React, { useState, useEffect, useRef } from "react";
import {
  Table,
  TableContainer,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Paper,
  TextField,
  IconButton,
  InputAdornment,
  TablePagination,
  Chip,
} from "@mui/material";
import Collapse from "@mui/material/Collapse";
import BoyIcon from "@mui/icons-material/Boy";
import WomanIcon from "@mui/icons-material/Woman";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import Fab from "@mui/material/Fab";
import LinearProgress from "@mui/material/LinearProgress";
import {
  getCompaniesAction,
  cleanCompanyAction,
  getCompanyAction,
  setOffset,
  setQuery,
} from "../../redux/actions/company";
import strings from "../../i18n/language";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import AddIcon from "@mui/icons-material/Add";
import CompanyDelete from "../../dialog/CompanyDelete";
import SearchIcon from "@mui/icons-material/Search";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";

const CompanyList = () => {
  const companies = useSelector((state) => state.company.companies);
  const user = useSelector((state) => state.user.user);
  const [companyDeleteId, setCompanyDeleteId] = useState(null);
  const [openModalDelete, setOpenModalDelete] = useState(false);
  const dispatch = useDispatch();
  const companiesRef = useRef([]);
  const navigate = useNavigate();
  const total = useSelector((state) => state.company.total);
  const limit = useSelector((state) => state.company.limit);

  const [page, setPage] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");
  const [searching, setSearching] = useState(false);
  const [moduleFilter, setModuleFilter] = useState(null);

  const isLoadingGetCompanies = useSelector(
    (state) => state.company.isLoadingGetCompanies
  );

  useEffect(() => {
    dispatch(getCompaniesAction());
  }, [dispatch]);

  useEffect(() => {
    if (companies) companiesRef.current = companies;
  }, [companies]);

  useEffect(() => {
    setSearching(true);
    setPage(0);
    const timeoutId = setTimeout(() => {
      setSearching(false);
      dispatch(setQuery(searchQuery));
    }, 1500);
    return () => clearTimeout(timeoutId);
  }, [searchQuery, dispatch]);

  const parseModules = (modules) => {
    if (Array.isArray(modules)) return modules;
    if (typeof modules === "string") { try { return JSON.parse(modules); } catch { return []; } }
    return [];
  };

  const Row = ({ company, setCompanyDeleteId, setOpenModalDelete }) => {
    const [open, setOpen] = useState(false);
    return (
      <>
        <TableRow key={company.id}>
          <TableCell>
            <IconButton
              aria-label="expand row"
              size="small"
              onClick={() => setOpen(!open)}
            >
              {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
            </IconButton>
          </TableCell>
          <TableCell>{company.name}</TableCell>
          <TableCell sx={{ whiteSpace: "nowrap" }}>
            <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
              {parseModules(company.modules).map((mod) => (
                <Chip key={mod} label={mod} size="small" color="primary" variant="outlined" />
              ))}
            </Box>
          </TableCell>
          <TableCell
            sx={{
              whiteSpace: "nowrap",
            }}
          >
            {company.phone}
          </TableCell>
          <TableCell
            sx={{
              whiteSpace: "nowrap",
            }}
          >
            {company.identityNumber}
          </TableCell>
          <TableCell>{company.mail}</TableCell>
          <TableCell>
            {company.Subscription?.PatientSubscriptions?.length}
          </TableCell>
          {user.type !== "viewer" && (
            <TableCell
              sx={{
                whiteSpace: "nowrap",
              }}
            >
              <IconButton
                aria-label="delete"
                onClick={() => {
                  setCompanyDeleteId(company.id);
                  setOpenModalDelete(true);
                }}
              >
                <DeleteIcon />
              </IconButton>
              <IconButton
                aria-label="edit"
                onClick={() => {
                  dispatch(getCompanyAction(company.id));
                  navigate("/companies/edit");
                }}
              >
                <EditIcon />
              </IconButton>
            </TableCell>
          )}
        </TableRow>
        <TableRow>
          <TableCell
            style={{ paddingBottom: 0, paddingTop: 0, borderBottom: "none" }}
            colSpan={6}
          >
            <Collapse in={open} timeout="auto" unmountOnExit>
              <Box sx={{ margin: 1 }}>
                <Typography variant="h6" gutterBottom component="div">
                  {strings.TITLE_PATIENTS_SUBSCRIPTION}
                </Typography>
                <Table size="small" aria-label="purchases">
                  <TableHead>
                    <TableRow>
                      <TableCell>{strings.LABEL_NAME}</TableCell>
                      <TableCell>{strings.LABEL_GENDER}</TableCell>
                      <TableCell>{strings.LABEL_IDENTITY}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {company.Subscription?.PatientSubscriptions?.map(
                      (patientSubscr) => (
                        <TableRow
                          key={
                            patientSubscr.PatientId +
                            "-" +
                            patientSubscr.SubscriptionId
                          }
                        >
                          <TableCell>
                            {patientSubscr.Patient?.displayName}
                          </TableCell>
                          <TableCell>
                            {" "}
                            {patientSubscr.Patient?.gender === "man" ? (
                              <BoyIcon></BoyIcon>
                            ) : (
                              <WomanIcon></WomanIcon>
                            )}
                          </TableCell>
                          <TableCell>
                            {patientSubscr.Patient?.identityNumber}
                          </TableCell>
                        </TableRow>
                      )
                    )}
                  </TableBody>
                </Table>
              </Box>
            </Collapse>
          </TableCell>
        </TableRow>
      </>
    );
  };

  return (
    <>
      <Paper sx={{ p: 2, width: "80%" }}>
        {searching && isLoadingGetCompanies && (
          <LinearProgress
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              width: "100%",
            }}
          />
        )}
        <TextField
          fullWidth
          variant="outlined"
          placeholder={strings.LABEL_SEARCH}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
            sx: { borderRadius: 25 },
          }}
          sx={{ mb: 2 }}
        />
        <Box sx={{ display: "flex", gap: 1, mb: 2 }}>
          {["Servital", "Laboral"].map((mod) => (
            <Chip
              key={mod}
              label={mod}
              color={moduleFilter === mod ? "primary" : "default"}
              variant={moduleFilter === mod ? "filled" : "outlined"}
              onClick={() => setModuleFilter(moduleFilter === mod ? null : mod)}
            />
          ))}
        </Box>
        {companies && (
          <TableContainer>
            <Table aria-label="Companies table">
              <TableHead>
                <TableRow>
                  <TableCell colSpan={2}>{strings.LABEL_NAME}</TableCell>
                  <TableCell>Modules</TableCell>
                  <TableCell>{strings.LABEL_PHONE}</TableCell>
                  <TableCell>{strings.LABEL_RIF}</TableCell>
                  <TableCell>{strings.LABEL_MAIL}</TableCell>
                  <TableCell>{strings.LABEL_TOTAL_PATIENTS}</TableCell>
                  {user.type !== "viewer" && (
                    <TableCell>{strings.LABEL_ACTION}</TableCell>
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                {companies
                  .filter((c) => !moduleFilter || parseModules(c.modules).includes(moduleFilter))
                  .map((company) => (
                    <Row
                      key={company.id}
                      company={company}
                      setCompanyDeleteId={setCompanyDeleteId}
                      setOpenModalDelete={setOpenModalDelete}
                    ></Row>
                  ))}
              </TableBody>
            </Table>
            <TablePagination
              rowsPerPageOptions={[20, 50, 100]}
              count={total}
              rowsPerPage={limit || 20}
              page={page}
              onPageChange={(event, newPage) => {
                dispatch(setOffset(limit * newPage));
                setPage(newPage);
              }}
              component="div"
            />
          </TableContainer>
        )}
      </Paper>
      {user.type !== "viewer" && (
        <Fab
          style={{ alignSelf: "flex-end", marginLeft: 10 }}
          color="primary"
          aria-label="add"
          onClick={() => {
            dispatch(cleanCompanyAction());
            navigate("/companies/edit");
          }}
        >
          <AddIcon />
        </Fab>
      )}
      <CompanyDelete
        open={openModalDelete}
        setOpen={setOpenModalDelete}
        companyDeleteId={companyDeleteId}
      ></CompanyDelete>
    </>
  );
};

export default CompanyList;
